// @ts-nocheck

import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

import Spritesheet from '../../../components/Spritesheet';
import {
  BANKROLL_CHANGES_ANIMATION_DURATION,
  BANKROLL_CHANGES_START_ANIM_DELAY,
} from '../../../components/UserBankroll';
import { ZIndices } from '../../../styled-components/dark/common';
import { type Point2D } from '../../../types/point';
import { normalize } from '../../../utils/number';

const ANIMATION_DURATION = BANKROLL_CHANGES_ANIMATION_DURATION - BANKROLL_CHANGES_START_ANIM_DELAY;
export const COINS_TRAVEL_ANIMATION_START_DELAY = 500; // ms

const SAFETY_UNMOUNTING_LAG_DELAY = 2000; // ms
const DELAY_BETWEEN_COINS = 30; // ms
const COIN_ROTATION_MIN_DURATION = 100; // ms
const COIN_ROTATION_MAX_DURATION = 800; // ms

const Wrapper = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  z-index: ${Math.max(ZIndices.navBar, ZIndices.tabBar) + 1};
  pointer-events: none;

  ${({ $hidden }) => css`
    opacity: ${$hidden ? 0 : 1};
  `}
`;

const getTranslationCoinAnimation = (
  xCircleOffset,
  yCircleOffset,
  xToOffset,
  yToOffset,
  $xOffset,
  $yOffset,
) => keyframes`
  0% {
    transform: translate(calc(-50%), calc(-50%));
    opacity: 0;
  }

  10% {
    opacity: 1;
  }

  30% {
    transform: translate(calc(-50% + ${xCircleOffset}em + ${$xOffset}px), calc(-50% + ${yCircleOffset}em + ${$yOffset}px));
    animation-timing-function: cubic-bezier(1, 0, 1, 1);
  }

  90% {
    opacity: 1;
    transform: translate(calc(${xToOffset}px - 45%), calc(${yToOffset}px - 40%));
  }

  100% {
    transform: translate(calc(${xToOffset}px - 45%), calc(${yToOffset}px - 40%));
    opacity: 0;
  }
`;

const StyledCoinSpritesheet = styled(Spritesheet)`
  &&&& {
    position: absolute;
    transform: translate(calc(-50%), calc(-50%));
    opacity: 0;

    ${({ $xFrom, $yFrom, $xCircleOffset, $yCircleOffset, $xToOffset, $yToOffset, $delay, $xOffset, $yOffset }) => css`
      left: ${$xFrom}px;
      top: ${$yFrom}px;
      animation: ${getTranslationCoinAnimation(
          $xCircleOffset,
          $yCircleOffset,
          $xToOffset,
          $yToOffset,
          $xOffset,
          $yOffset,
        )}
        ${ANIMATION_DURATION}ms ease-out ${$delay}ms forwards;
    `}
  }
`;

type AnimatedCoinProps = {
  index: number;
  nbCoins: number;
  xFrom: number;
  yFrom: number;
  xToOffset: number;
  yToOffset: number;
  spritesheetAsset: string;
};

const AnimatedCoin = React.memo(function AnimatedCoin({
  index,
  nbCoins,
  xFrom,
  yFrom,
  xToOffset,
  yToOffset,
  spritesheetAsset,
}: AnimatedCoinProps) {
  const radAngle = useMemo(() => (index / (nbCoins - 1)) * 360, [index, nbCoins]);
  const coinRadius = useMemo(() => normalize(nbCoins, 1, 15, 1.5, 2.5), [nbCoins]);
  const xChaosCorrection = useMemo(
    () => Math.floor((Math.floor(Math.random() * 2) ? 1 : -1) * (Math.random() * coinRadius) * 1000) / 1000,
    [coinRadius],
  );
  const yChaosCorrection = useMemo(
    () => Math.floor((Math.floor(Math.random() * 2) ? 1 : -1) * (Math.random() * coinRadius) * 1000) / 1000,
    [coinRadius],
  );
  const xCircleOffset = useMemo(
    () => (index ? coinRadius * Math.sin(radAngle) + xChaosCorrection : xChaosCorrection),
    [index, radAngle, xChaosCorrection, coinRadius],
  );
  const yCircleOffset = useMemo(
    () => (index ? coinRadius * Math.cos(radAngle) + yChaosCorrection : yChaosCorrection),
    [index, radAngle, yChaosCorrection, coinRadius],
  );
  const xOffset = useMemo(() => {
    if (xFrom > coinRadius * 16 && xFrom < window.innerWidth - coinRadius * 16) {
      return 0;
    }
    if (xFrom < coinRadius * 16) {
      return coinRadius * 16;
    }
    return -1 * (coinRadius * 16);
  }, [coinRadius, xFrom]);
  const yOffset = useMemo(() => {
    if (yFrom > coinRadius * 16 && yFrom < window.innerHeight - coinRadius * 16) {
      return 0;
    } else if (yFrom < coinRadius * 16) {
      return coinRadius * 16;
    } else {
      return -1 * (coinRadius * 16);
    }
  }, [coinRadius, yFrom]);
  const rotationDuration = useMemo(
    () =>
      Math.floor(
        COIN_ROTATION_MIN_DURATION + Math.random() * (COIN_ROTATION_MAX_DURATION - COIN_ROTATION_MIN_DURATION),
      ),
    [],
  );
  const coinDelay = useMemo(() => COINS_TRAVEL_ANIMATION_START_DELAY + index * DELAY_BETWEEN_COINS, [index]);

  return (
    <StyledCoinSpritesheet
      columnCount={24}
      rowCount={1}
      url={spritesheetAsset}
      keepRatio
      play={1}
      duration={rotationDuration}
      height={25}
      loop
      $delay={coinDelay}
      $xFrom={xFrom}
      $yFrom={yFrom}
      $xCircleOffset={xCircleOffset}
      $yCircleOffset={yCircleOffset}
      $xToOffset={xToOffset}
      $yToOffset={yToOffset}
      $xOffset={xOffset}
      $yOffset={yOffset}
    />
  );
});

type CoinsTravelAnimationProps = {
  className?: string;
  nbCoins?: number;
  onAnimationDone?: () => void;
  from: Point2D;
  to: Point2D;
  arrivalElementId?: string;
  spritesheetAsset: string;
};

const CoinsTravelAnimation = ({
  className = '',
  nbCoins = 1,
  onAnimationDone = () => {},
  from,
  to,
  arrivalElementId,
  spritesheetAsset,
}: CoinsTravelAnimationProps) => {
  const mainAnimPidRef = useRef();
  const safetyDelayPidRef = useRef();
  const xFrom = useMemo(() => from.x, [from]);
  const yFrom = useMemo(() => from.y, [from]);
  const xTo = useMemo(() => to.x, [to]);
  const yTo = useMemo(() => to.y, [to]);
  const xToOffset = useMemo(() => xTo - xFrom, [xFrom, xTo]);
  const yToOffset = useMemo(() => yTo - yFrom, [yFrom, yTo]);
  const hasScheduledArrivalAnim = useRef(false);
  const arrivalAnimPidsRef = useRef([]);
  const [shouldHideAnim, setShouldHideAnim] = useState(false);

  useLayoutEffect(() => {
    if (onAnimationDone && nbCoins && !mainAnimPidRef.current) {
      mainAnimPidRef.current = setTimeout(
        () => {
          setShouldHideAnim(true);
          safetyDelayPidRef.current = setTimeout(() => {
            mainAnimPidRef.current = null;
            onAnimationDone();
          }, SAFETY_UNMOUNTING_LAG_DELAY);
        },
        ANIMATION_DURATION + (nbCoins - 1) * DELAY_BETWEEN_COINS + COINS_TRAVEL_ANIMATION_START_DELAY,
      );
    }
  }, [onAnimationDone, nbCoins]);

  useLayoutEffect(() => {
    return () => {
      if (mainAnimPidRef.current) {
        clearTimeout(mainAnimPidRef.current);
        mainAnimPidRef.current = null;
      }

      if (safetyDelayPidRef.current) {
        clearTimeout(safetyDelayPidRef.current);
        safetyDelayPidRef.current = null;
      }
    };
  }, []);

  const shouldRender = useMemo(() => {
    return (
      typeof xFrom === 'number' &&
      typeof yFrom === 'number' &&
      typeof xToOffset === 'number' &&
      typeof yToOffset === 'number' &&
      typeof nbCoins === 'number' &&
      nbCoins > 0
    );
  }, [xFrom, yFrom, xToOffset, yToOffset, nbCoins]);

  useLayoutEffect(() => {
    if (!hasScheduledArrivalAnim.current && nbCoins && arrivalElementId) {
      const arrivalElement = document.getElementById(arrivalElementId);

      if (arrivalElement) {
        hasScheduledArrivalAnim.current = true;

        for (let i = 0; i < nbCoins; i++) {
          arrivalAnimPidsRef.current.push(
            setTimeout(
              () => {
                arrivalElement.style.transform = `scale(1.15)`;

                arrivalAnimPidsRef.current.push(
                  setTimeout(
                    () => {
                      arrivalElement.style.transform = 'scale(1)';
                    },
                    Math.floor(DELAY_BETWEEN_COINS / 2) * 3,
                  ),
                );
              },
              ANIMATION_DURATION + i * DELAY_BETWEEN_COINS + COINS_TRAVEL_ANIMATION_START_DELAY * 0.3,
            ),
          );
        }
      }
    }
  }, [nbCoins, arrivalElementId]);

  useLayoutEffect(() => {
    return () => {
      if (arrivalAnimPidsRef.current.length) {
        for (let i = 0; i < arrivalAnimPidsRef.current.length; i++) {
          // eslint-disable-next-line react-hooks/exhaustive-deps
          const pid = arrivalAnimPidsRef.current[i];
          clearTimeout(pid);
        }
        arrivalAnimPidsRef.current = [];
      }
    };
  }, []);

  return (
    <Wrapper className={className} $hidden={shouldHideAnim}>
      {shouldRender
        ? [...new Array(nbCoins).keys()].map((i) => (
            <AnimatedCoin
              key={`animated-coin-${i}`}
              index={i}
              nbCoins={nbCoins}
              xFrom={xFrom}
              yFrom={yFrom}
              xToOffset={xToOffset}
              yToOffset={yToOffset}
              spritesheetAsset={spritesheetAsset}
            />
          ))
        : null}
    </Wrapper>
  );
};

export default React.memo<unknown>(CoinsTravelAnimation);
